
/**
 * Table Detail Component
 * The table detail module is going to be use on the PI pages.
 * Inside the module, the author can configure:
 *  - headline (string)
 *  - labelBackMobile (string)
 * The API will add further information:
 *  - insideAccordion (boolean)
 *  - footnotes (Footnote[])
 *
 * The table detail component will render table cell slots inside.
 * It will render either as a standalone table or behave like an accordion,
 * depending on what setting the author has used on the table module.
 *
 * @vue-prop {string} datajson - Config and Component's data coming from CMS
 *
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { TableDetailData, TableDetailMethods, TableDetailComputed, TableDetailProps } from './TableDetail';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { getLocalFootnotes, setModuleId } from '../../_utils/Footnotes/FootnotesHelper';
import { WB_RESIZE_EVENT_NAME } from '../../_utils/MediaQueryService/MediaQueryUtils';
import { isMobileLayout, updateLayout } from './TableUtils';

const parentIds = new Map();
let currentParentIdIteration = 1;

export default ComponentInitializeDispatcher.extend<
  TableDetailData,
  TableDetailMethods,
  TableDetailComputed,
  TableDetailProps
>({
  name: 'owc-table-detail',
  components: {
    SlotContainer,
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent],
  mounted(): void {
    window.addEventListener(
      WB_RESIZE_EVENT_NAME,
      (updateLayout as unknown) as EventListenerOrEventListenerObject,
    );
  },
  beforeDestroy(): void {
    window.removeEventListener(
      WB_RESIZE_EVENT_NAME,
      (updateLayout as unknown) as EventListenerOrEventListenerObject,
    );
  },
  computed: {
    headline: function() {
      return this.componentData?.headline;
    },
    backLabel: function() {
      return this.componentData?.labelBackMobile;
    },
    isAccordion: function() {
      return this.componentData?.insideAccordion;
    },
    accordionIndex() {
      return parentIds.get(this.componentData.parentAccordionId);
    },
    footnotes() {
      const footnotes = getLocalFootnotes(this.componentData);
      return footnotes && footnotes?.length > 0 ? footnotes : undefined;
    },
    shouldShowLocalFootnotes() {
      return !!(this.footnotes || isMobileLayout);
    },
    moduleId() {
      return setModuleId(this);
    },
  },
  methods: {
    handleAccordionClick: function() {
      if (this.accordionIndex) {
        this.handleCustomTracking(
          this.$el,
          `accordion${this.accordionIndex}.${this.headline}`,
          'link',
          'click',
        );
      }
    },
  },
  watch: {
    componentData(data) {
      if (data.parentAccordionId) {
        const currentIdx = parentIds.has(data.parentAccordionId);

        if (this.isAccordion && !currentIdx) {
          parentIds.set(data.parentAccordionId, currentParentIdIteration);
          currentParentIdIteration += 1;
        }
      }
    },
  },
});
